
import React, { useState, useEffect, useRef } from 'react';
import { Menu, X, LogIn, Car, Briefcase, UserPlus, Martini, Disc, ShieldAlert, Users } from './ui/Icons';
import { Button } from './ui/Button';
import { Link, useRouter } from '../lib/router';

// Feature flag: set to true to show Master Admin link in the navbar
const SHOW_MASTER_ADMIN = false;

export const Navbar: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const loginRef = useRef<HTMLDivElement>(null);
  const { path } = useRouter();
  const { push } = useRouter();
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setLoginOpen(false);
    // update logged-in state when route changes (in case of login/logout)
    try { setIsLoggedIn(Boolean(sessionStorage.getItem('guestLoggedIn'))); } catch (e) {}
  }, [path]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (loginRef.current && !loginRef.current.contains(event.target as Node)) {
        setLoginOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    // listen to storage events so other tabs update the menu
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'guestLoggedIn') {
        try { setIsLoggedIn(Boolean(sessionStorage.getItem('guestLoggedIn'))); } catch (err) {}
      }
    };
    window.addEventListener('storage', onStorage);
    try { setIsLoggedIn(Boolean(sessionStorage.getItem('guestLoggedIn'))); } catch (e) {}
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const navLinks = [
    { name: 'Events', href: '/events' },
    { name: 'Pulse', href: '/pulse' },
    { name: 'Features', href: '/features' },
    { name: 'Partner', href: '/partner' },
    { name: 'Vision', href: '/vision' },
  ];

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled || path !== '/' ? 'py-4 bg-black/60 backdrop-blur-xl border-b border-white/5' : 'py-6 bg-transparent'}`}>
      <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
        <Link to="/" className="text-2xl font-bold tracking-tighter flex items-center gap-1.5">
          <img src="/images/logo-navbar.png" alt="Night Navi" className="w-16 md:w-20 h-16 md:h-20 object-contain" />
          <span className="-ml-5 text-transparent bg-clip-text bg-gradient-to-tr from-[#eb46ff] to-[#5d3bee]">Night Navi</span>
          <span className="translate-y-1 text-xs font-semibold tracking-wide text-white/95">Beta</span>
        </Link>

        <div className="hidden md:flex items-center gap-8">
          <div className="flex gap-6">
            {navLinks.map((link) => (
              <Link key={link.name} to={link.href} className="text-sm font-medium text-gray-300 hover:text-white transition-colors">
                {link.name}
              </Link>
            ))}
          </div>
          
          <div className="h-6 w-px bg-white/10"></div>

          <div className="flex items-center gap-3">
            <Link to="/download">
              <Button variant="secondary" className="px-4 py-2 text-xs">App laden</Button>
            </Link>
            
            <div className="relative" ref={loginRef}>
              {!isLoggedIn ? (
                <>
                  <button 
                    onClick={() => setLoginOpen(!loginOpen)}
                    className={`flex items-center gap-2 text-xs font-medium transition-colors px-2 py-2 ${loginOpen ? 'text-white' : 'text-gray-400 hover:text-white'}`}
                  >
                    <LogIn className="w-4 h-4" />
                    Login
                  </button>

                  {loginOpen && (
                    <div className="absolute top-full right-0 mt-3 w-64 bg-night-950/95 backdrop-blur-xl border border-white/10 rounded-xl shadow-2xl p-2 animate-fade-up overflow-hidden">
                      <div className="flex flex-col gap-1">
                        <Link to="/login">
                          <button className="flex items-center gap-3 w-full text-left px-4 py-3 text-sm text-gray-300 hover:text-white hover:bg-white/5 rounded-lg transition-colors group">
                             <div className="p-1.5 bg-green-500/10 rounded-md text-green-400 group-hover:bg-green-500 group-hover:text-black transition-colors">
                              <Users className="w-4 h-4" />
                            </div>
                            Login
                          </button>
                        </Link>

                        <div className="h-px bg-white/10 my-1 mx-2"></div>

                        <Link to="/partner?action=register">
                          <button className="flex items-center gap-3 w-full text-left px-4 py-3 text-sm text-gray-300 hover:text-white hover:bg-white/5 rounded-lg transition-colors group">
                             <div className="p-1.5 bg-purple-500/10 rounded-md text-purple-400 group-hover:bg-purple-500 group-hover:text-black transition-colors">
                              <Briefcase className="w-4 h-4" />
                            </div>
                            Partner werden
                          </button>
                        </Link>

                        <Link to="/bar-login">
                          <button className="flex items-center gap-3 w-full text-left px-4 py-3 text-sm text-gray-300 hover:text-white hover:bg-white/5 rounded-lg transition-colors group">
                             <div className="p-1.5 bg-pink-500/10 rounded-md text-pink-400 group-hover:bg-pink-500 group-hover:text-black transition-colors">
                              <Martini className="w-4 h-4" />
                            </div>
                            Bar Login
                          </button>
                        </Link>

                        <Link to="/organizer-login">
                          <button className="flex items-center gap-3 w-full text-left px-4 py-3 text-sm text-gray-300 hover:text-white hover:bg-white/5 rounded-lg transition-colors group">
                             <div className="p-1.5 bg-blue-500/10 rounded-md text-blue-400 group-hover:bg-blue-500 group-hover:text-black transition-colors">
                              <Briefcase className="w-4 h-4" />
                            </div>
                            Veranstalter Login
                          </button>
                        </Link>

                        <div className="h-px bg-white/10 my-1 mx-2"></div>

                        {SHOW_MASTER_ADMIN && (
                          <Link to="/master-login">
                            <button className="flex items-center gap-3 w-full text-left px-4 py-3 text-sm text-gray-300 hover:text-white hover:bg-white/5 rounded-lg transition-colors group bg-red-900/10">
                               <div className="p-1.5 bg-red-500/10 rounded-md text-red-400 group-hover:bg-red-500 group-hover:text-black transition-colors">
                                <ShieldAlert className="w-4 h-4" />
                              </div>
                              Master Admin
                            </button>
                          </Link>
                        )}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <button onClick={() => setLoginOpen(!loginOpen)} className={`flex items-center gap-2 text-xs font-medium transition-colors px-2 py-2 ${loginOpen ? 'text-white' : 'text-gray-400 hover:text-white'}`}>
                    <Users className="w-4 h-4" />
                    Mein Account
                  </button>

                  {loginOpen && (
                    <div className="absolute top-full right-0 mt-3 w-56 bg-night-950/95 backdrop-blur-xl border border-white/10 rounded-xl shadow-2xl p-2 animate-fade-up overflow-hidden">
                      <div className="flex flex-col gap-1">
                        <Link to="/account/tickets">
                          <button className="flex items-center gap-3 w-full text-left px-4 py-3 text-sm text-gray-300 hover:text-white hover:bg-white/5 rounded-lg transition-colors">
                            <div className="p-1.5 bg-green-500/10 rounded-md text-green-400"><Users className="w-4 h-4" /></div>
                            Meine Tickets
                          </button>
                        </Link>
                        <Link to="/account/profile">
                          <button className="flex items-center gap-3 w-full text-left px-4 py-3 text-sm text-gray-300 hover:text-white hover:bg-white/5 rounded-lg transition-colors">
                            <div className="p-1.5 bg-white/5 rounded-md text-gray-300"><Briefcase className="w-4 h-4" /></div>
                            Account bearbeiten
                          </button>
                        </Link>
                        <div className="h-px bg-white/10 my-1 mx-2"></div>
                        <button onClick={() => { try { sessionStorage.removeItem('guestLoggedIn'); sessionStorage.removeItem('guestId'); localStorage.removeItem('guestEmail'); } catch(e){} setIsLoggedIn(false); push('/'); }} className="w-full text-left px-4 py-3 text-sm text-red-400 hover:text-white hover:bg-white/5 rounded-lg">Ausloggen</button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        <button 
          className="md:hidden text-white"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          {mobileMenuOpen ? <X /> : <Menu />}
        </button>
      </div>

      {mobileMenuOpen && (
        <div className="absolute top-full left-0 right-0 bg-black/95 backdrop-blur-2xl border-b border-white/10 p-6 md:hidden flex flex-col gap-6 animate-fade-up h-screen overflow-y-auto pb-20">
          {navLinks.map((link) => (
            <Link 
              key={link.name} 
              to={link.href} 
              className="text-2xl font-medium text-gray-200"
            >
              {link.name}
            </Link>
          ))}
          <div className="mt-4 space-y-3 pt-6 border-t border-white/10">
            <Link to="/download" className="block">
              <Button className="w-full justify-center">App laden</Button>
            </Link>
            
            <div className="pt-4 space-y-2">
              <p className="text-xs font-semibold text-gray-500 uppercase px-1">Login & Registrierung</p>

               <Link to="/login" className="block">
                <Button variant="outline" className="w-full justify-start gap-3 text-white border-green-500/30 bg-green-500/10 hover:bg-green-500/20">
                  <Users className="w-4 h-4 text-green-400" /> Login
                </Button>
              </Link>

               <div className="h-px bg-white/10 my-1 mx-2"></div>

               <Link to="/partner?action=register" className="block">
                <Button variant="outline" className="w-full justify-start gap-3 text-white border-purple-500/30 bg-purple-500/10 hover:bg-purple-500/20">
                  <Briefcase className="w-4 h-4 text-purple-400" /> Partner werden
                </Button>
              </Link>

              {/* Club Login entfernt aus mobile menu */}

              <Link to="/bar-login" className="block">
                <Button variant="outline" className="w-full justify-start gap-3 text-gray-300 border-white/10 hover:bg-white/5">
                  <Martini className="w-4 h-4 text-pink-400" /> Als Bar anmelden
                </Button>
              </Link>

              <Link to="/organizer-login" className="block">
                <Button variant="outline" className="w-full justify-start gap-3 text-gray-300 border-white/10 hover:bg-white/5">
                  <Briefcase className="w-4 h-4 text-blue-400" /> Als Veranstalter anmelden
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};
